import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

interface Notification {
  id?: string;
  _id?: string;
  title?: string;
  message: string;
  type?: string;
  isRead?: boolean;
  read?: boolean;
  createdAt?: string;
  time?: string;
  vehicleId?: string;
  totalCost?: number;
}

const isNotificationRead = (notification: Notification): boolean => {
  return notification.isRead === true || notification.read === true;
};

interface NotificationsTableProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onNotificationClick: (notification: Notification) => void;
}

export default function NotificationsTable({
  notifications,
  onMarkAsRead,
  onNotificationClick,
}: NotificationsTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  const unreadNotifications = notifications.filter(n => !isNotificationRead(n));

  const getVehicleNumber = (message: string): string => {
    const match = message.match(/[A-Z]{2}-\w+-\d+/);
    return match ? match[0] : '';
  };

  const getServiceType = (message: string): string => {
    if (message.includes('Service due')) return 'Service Due';
    if (message.includes('Oil Change')) return 'Oil Change';
    if (message.includes('Greasing')) return 'Greasing';
    if (message.includes('service completed')) return 'Service Completed';
    return 'Service';
  };

  const getStatus = (type?: string): { label: string; color: string } => {
    if (type === 'warning' || type === 'Urgent' || type === 'oil') {
      return { label: 'Pending', color: '#ff9800' };
    }
    return { label: 'New', color: '#2196f3' };
  };

  const getTotalCost = (message: string): string => {
    return '-';
  };

  // Mobile/Tablet card view
  if (isMobile || isTablet) {
    return (
      <Box sx={{ width: '100%' }}>
        {unreadNotifications.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No unread notifications
            </Typography>
          </Paper>
        ) : (
          unreadNotifications.map((notification) => {
            const status = getStatus(notification.type);
            return (
              <Paper
                key={notification.id || notification._id || ''}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  backgroundColor: '#e3f2fd',
                  cursor: 'pointer',
                }}
                onClick={() => onNotificationClick(notification)}
              >
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DirectionsCarIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {getVehicleNumber(notification.message) || notification.title}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      backgroundColor: status.color + '20',
                      color: status.color,
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  >
                    {status.label}
                  </Typography>
                </Box>

                {/* Details - 2 columns for tablet, 1 for mobile */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: isTablet ? '1fr 1fr' : '1fr',
                  gap: 1,
                  mt: 1
                }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Service</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {getServiceType(notification.message)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Total Cost</Typography>
                    <Typography variant="body2">
                      {getTotalCost(notification.message)}
                    </Typography>
                  </Box>
                  {isTablet && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">Message</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {notification.message}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            );
          })
        )}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        overflowX: 'auto',
      }}
    >
      <Table sx={{ minWidth: 600 }} aria-label="notifications table">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#062141' }}>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Vehicle</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Service Message</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Cost</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {unreadNotifications.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  No unread notifications
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            unreadNotifications.map((notification) => {
              const status = getStatus(notification.type);
              return (
                <TableRow
                  key={notification.id || ''}
                  sx={{
                    '&:nth-of-type(even)': { backgroundColor: '#f8f9fa' },
                    '&:hover': { backgroundColor: '#e3f2fd', cursor: 'pointer' },
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: '#e3f2fd',
                  }}
                  onClick={() => onNotificationClick(notification)}
                >
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DirectionsCarIcon fontSize="small" color="primary" />
                      {getVehicleNumber(notification.message) || notification.title}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{getServiceType(notification.message)}</Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {notification.message}
                    </Typography>
                  </TableCell>
                  <TableCell>{getTotalCost(notification.message)}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'inline-block',
                        backgroundColor: status.color + '20',
                        color: status.color,
                        fontWeight: 600,
                      }}
                    >
                      {status.label}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
